import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserCircle, Building2 } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { useBranding } from '@/contexts/BrandingContext';
import { useLanguage } from '@/i18n/LanguageContext';
import Captcha from '@/components/Captcha';
import { analytics } from '@/lib/analytics';

function StaffLogin() {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const captchaRef = useRef(null);
  const formStartedRef = useRef(false);
  
  useEffect(() => {
    checkAuth();
  }, []);
  
  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      if (response.data.role === 'staff') {
        navigate('/dashboard');
      }
    } catch (error) {
      // Not authenticated
    }
  };
  
  const handleCaptchaVerify = (data) => {
    setCaptchaVerified(true);
    setError('');
  };
  
  const handleCaptchaError = (errorMsg) => {
    setCaptchaVerified(false);
    setError(errorMsg);
  };

  const markFormStart = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    analytics.formStart('staff_login');
  };
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    analytics.formSubmit('staff_login');
    analytics.loginSubmit('staff');
    
    // Verify math captcha if present
    if (captchaRef.current && !captchaVerified) {
      const captchaInput = document.querySelector('[data-testid="captcha-answer-input"]');
      if (captchaInput && !captchaInput.value) {
        setError('Please solve the CAPTCHA');
        setLoading(false);
        return;
      }
      const expectedEl = document.querySelector('input[name="captcha_expected"]');
      const answerEl = document.querySelector('input[name="captcha_answer"]');
      if (expectedEl && answerEl) {
        if (parseInt(answerEl.value) !== parseInt(expectedEl.value)) {
          setError('Incorrect CAPTCHA answer. Please try again.');
          setLoading(false);
          return;
        }
      }
    }
    
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
        device_type: 1  // 1=browser, 2=mobile, 3=desktop
      }, {
        withCredentials: true
      });
      
      if (response.data.role === 'staff') {
        analytics.loginSuccess('staff');
        // Persist token in localStorage so mobile browsers don't lose the session
        if (response.data.session_token) {
          localStorage.setItem('staff_session_token', response.data.session_token);
        }
        // Store staff_type in localStorage for manager detection
        if (response.data.staff_settings?.staff_type) {
          localStorage.setItem('staff_type', response.data.staff_settings.staff_type);
        }
        navigate('/dashboard');
      } else {
        setError('Access denied. Staff role required.');
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-100 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            {branding.logo_url ? (
              <img 
                src={branding.logo_url} 
                alt={branding.platform_name} 
                className="h-16 object-contain"
              />
            ) : (
              <div className="p-4 bg-blue-600 rounded-full">
                <Building2 className="w-12 h-12 text-white" />
              </div>
            )}
          </div>
          {/* Platform name hidden when logo is present */}
          {!branding.logo_url && (
            <h1 className="text-3xl font-bold text-gray-900">{branding.platform_name}</h1>
          )}
          <p className="text-gray-600 mt-2">{t('auth.staff_login')}</p>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>{t('nav.welcome')}</CardTitle>
            <CardDescription>{t('auth.staff_login')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} onFocus={markFormStart} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="staff-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="staff-password-input"
                />
              </div>
              
              {/* CAPTCHA */}
              <div ref={captchaRef}>
                <Captcha 
                  loginType="staff"
                  onVerify={handleCaptchaVerify}
                  onError={handleCaptchaError}
                />
              </div>
              
              {error && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded" data-testid="error-message">
                  {error}
                </div>
              )}
              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
                data-testid="staff-login-button"
              >
                {loading ? t('common.loading') : t('auth.login_btn')}
              </Button>
            </form>
            
            <div className="mt-6 text-center text-sm text-gray-600">
              <p>{t('auth.no_account')}</p>
              <p className="mt-1">Contact your company admin to get access.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default StaffLogin;
